import {
  useCallback,
  useEffect,
  useState,
  useTransition,
} from 'react';
import { ContentContainer } from '../../../components/Filters/components/ContentContainer';
import { ControlsPanel } from '../../../components/Filters/components/ControlsPanel';
import { FilterLoader } from '../../../components/Filters/components/FilterLoader';
import { TreeSelector } from '../../../components/TreeSelector/TreeSelector';
import { API } from '../../../API';
import { showRequestError } from '../../../utils/requestError';
import { useSearchContext } from '../contexts';

export interface GeoRegion {
  id: number;
  name: string;
  category: {
    id: string;
    name: string;
  };
}

export const GeoGroupChooser = () => {
  const { state, updateDraftFilters } = useSearchContext();
  const [geoRegionsData, setGeoRegionsData] = useState<GeoRegion[] | null>(null);
  const [isPending, startTransition] = useTransition();

  // Use draft filters from context
  const selected = state.draftFilters.geoGroup.value;

  const getGeoRegions = useCallback(() => {
    startTransition(() => {
      API.fetch('GET', `/API/v2/country-groups?category_type=GEO`)
        .then((data) => {
          setGeoRegionsData(data);
        })
        .catch((error) => {
          showRequestError(error);
        });
    });
  }, []);

  useEffect(() => {
    getGeoRegions();
  }, [getGeoRegions]);

  const handleChange = useCallback((selectedIds: (keyof GeoRegion)[]) => {
    updateDraftFilters({
      geoGroup: {
        value: selectedIds.map(id => Number(id)),
        exclude: state.draftFilters.geoGroup.exclude,
      },
    });
  }, [state.draftFilters.geoGroup.exclude, updateDraftFilters]);

  const handleClearAll = () => {
    updateDraftFilters({
      geoGroup: {
        value: [],
        exclude: false,
      },
    });
  };

  return (
    <ContentContainer
      actionsComponent={
        <ControlsPanel
          id="geo-group-filter"
          count={state.draftFilters.geoGroup.value.length}
          isExclude={state.draftFilters.geoGroup.exclude}
          showExcludeSwitch={true}
          showSelectAll={false}
          onClearAll={handleClearAll}
          onExcludeClick={() => {
            updateDraftFilters({
              geoGroup: {
                value: state.draftFilters.geoGroup.value,
                exclude: !state.draftFilters.geoGroup.exclude,
              },
            });
          }}
        />
      }
    >
      {isPending && !geoRegionsData ? (
        <FilterLoader />
      ) : (
        <TreeSelector<GeoRegion>
          items={geoRegionsData}
          selectedIds={selected as unknown as (keyof GeoRegion)[]}
          onChange={handleChange}
          idKey="id"
          labelKey="name"
          multiple={true}
          disabled={false}
          groupBy={(item: GeoRegion) => item.category.name}
          mode="modern"
        />
      )}
    </ContentContainer>
  );
};
